// A branched menu: a trunk line with curved SVG branches to each child, a marker that glides to the open
// heading, and a fold that opens each section. Adapted from React Bits' Branched Menu (reactbits.dev/c/micro/branched-menu):
// children are real links (deep links + middle-click work), `active` is controlled (the Guide's scroll-spy drives it),
// icons are optional per child. Colors and type come from tokens.css (branched-menu.css).

import { useLayoutEffect, useRef, useState, type ComponentType, type CSSProperties } from 'react';
import './branched-menu.css';
import { COARSE_MQ, useMedia } from './mobile';

export interface BranchedChild {
  value: string;
  label: string;
  href: string;
  /** A Tabler icon shown before the label. */
  icon?: ComponentType<{ size?: number; stroke?: number; 'aria-hidden'?: boolean }>;
}
export interface BranchedSection {
  label: string;
  children: BranchedChild[];
}

const PAD = 6;
const MARK = 16;

export function BranchedMenu({
  sections,
  active,
  label,
  rowHeight,
  indent = 40,
  trunk = 14,
  radius = 10,
  onSelect,
}: {
  sections: BranchedSection[];
  active: string;
  label: string;
  /** Default: 36, and a finger's 44 on touch screens. */
  rowHeight?: number;
  indent?: number;
  trunk?: number;
  radius?: number;
  onSelect?: (value: string) => void;
}) {
  const coarse = useMedia(COARSE_MQ);
  rowHeight ??= coarse ? 44 : 36;
  const activeSection = sections.findIndex((s) => s.children.some((c) => c.value === active));
  const [open, setOpen] = useState<Set<number>>(() => new Set(activeSection >= 0 ? [activeSection] : [0]));
  const navRef = useRef<HTMLElement>(null);
  const heads = useRef<(HTMLButtonElement | null)[]>([]);
  const markerRef = useRef<HTMLSpanElement>(null);

  // reading into a new section opens it (the reader's own folds stay as they are)
  useLayoutEffect(() => {
    if (activeSection >= 0) setOpen((prev) => (prev.has(activeSection) ? prev : new Set(prev).add(activeSection)));
  }, [activeSection]);

  const markerShown = activeSection >= 0 && open.has(activeSection);
  useLayoutEffect(() => {
    const place = (glide: boolean) => {
      const m = markerRef.current;
      const el = heads.current[activeSection];
      if (!m) return;
      const on = markerShown && el;
      if (!glide) m.style.transition = 'none';
      if (on) m.style.top = `${el.offsetTop + (el.offsetHeight - MARK) / 2}px`;
      m.toggleAttribute('data-on', Boolean(on));
      if (!glide) {
        void m.offsetHeight;
        m.style.transition = '';
      }
    };
    place(true);
    let first = true;
    const ro = new ResizeObserver(() => {
      if (first) {
        first = false;
        return;
      }
      place(false);
    });
    if (navRef.current) ro.observe(navRef.current);
    return () => ro.disconnect();
  }, [activeSection, markerShown, sections, rowHeight]);

  const toggle = (i: number) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });

  const r = Math.min(radius, rowHeight / 2 - 2);
  const endX = indent - 8;
  const rowY = (k: number) => PAD + k * rowHeight + rowHeight / 2;
  const branch = (k: number) => `M ${trunk} ${rowY(k) - r} A ${r} ${r} 0 0 0 ${trunk + r} ${rowY(k)} H ${endX}`;
  const reach = (k: number) => `M ${trunk} 0 V ${rowY(k) - r} A ${r} ${r} 0 0 0 ${trunk + r} ${rowY(k)} H ${endX}`;
  const length = (k: number) => rowY(k) - r + (Math.PI * r) / 2 + (endX - trunk - r);

  return (
    <nav
      ref={navRef}
      className="branched-menu"
      aria-label={label}
      style={{ '--bm-row': `${rowHeight}px`, '--bm-indent': `${indent}px` } as CSSProperties}
    >
      <span ref={markerRef} className="branched-menu__marker" aria-hidden="true" />
      {sections.map((sec, i) => {
        const isOpen = open.has(i);
        const bodyH = PAD * 2 + sec.children.length * rowHeight;
        const id = `bm-${i}`;
        return (
          <div key={sec.label} className="branched-menu__section" data-open={isOpen ? '' : undefined}>
            <button
              ref={(el) => {
                heads.current[i] = el;
              }}
              type="button"
              className="branched-menu__head"
              aria-expanded={isOpen}
              aria-controls={id}
              data-current={i === activeSection ? '' : undefined}
              onClick={() => toggle(i)}
            >
              {sec.label}
            </button>
            <div className="branched-menu__body" id={id}>
              <div className="branched-menu__fold">
                <div className="branched-menu__tree" style={{ height: bodyH }}>
                  <svg className="branched-menu__lines" width={indent} height={bodyH} aria-hidden="true">
                    <path className="branched-menu__base" d={`M ${trunk} 0 V ${rowY(sec.children.length - 1) - r}`} />
                    {sec.children.map((kid, k) => (
                      <path key={kid.value} className="branched-menu__base" d={branch(k)} />
                    ))}
                    {sec.children.map((kid, k) => (
                      <path
                        key={kid.value}
                        className="branched-menu__reach"
                        d={reach(k)}
                        style={{ strokeDasharray: length(k), strokeDashoffset: kid.value === active ? 0 : length(k) }}
                      />
                    ))}
                  </svg>
                  {sec.children.map((kid) => (
                    <a
                      key={kid.value}
                      href={kid.href}
                      className="branched-menu__item"
                      aria-current={kid.value === active ? 'location' : undefined}
                      tabIndex={isOpen ? 0 : -1}
                      onClick={() => onSelect?.(kid.value)}
                    >
                      {kid.icon && <kid.icon size={16} stroke={1.5} aria-hidden />}
                      <span className="branched-menu__label">{kid.label}</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}
